import { c as _c } from "react/compiler-runtime";
import { faCircleXmark, faDownload } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { mergeTailwindClasses } from '@xefi/x-react/utils';
import { t } from 'i18next';
import { type InputHTMLAttributes, useMemo } from 'react';
import { type Accept, type FileRejection, useDropzone } from 'react-dropzone';
import { useToasts } from '@/hooks/useToasts';
import { FileCard } from './FileCard/FileCard';
type FileUploaderProps = {
  authorizedFiles?: Accept | string;
  isMultiple?: boolean;
  files: File[];
  setFiles: (files: File[]) => void;
  containerClass?: string;
  handleRemoveFile: (file: File) => void;
  isAlert?: boolean;
  isLoadingFile?: boolean;
  maxSizeInMo?: number;
  disabled?: boolean;
} & Omit<InputHTMLAttributes<HTMLInputElement>, 'onDrop'>;
export const FileUploader = t0 => {
  const $ = _c(58);
  let authorizedFiles;
  let containerClass;
  let files;
  let handleRemoveFile;
  let maxSizeInMo;
  let rest;
  let setFiles;
  let t1;
  let t2;
  let t3;
  let t4;
  if ($[0] !== t0) {
    ({
      authorizedFiles,
      isMultiple: t1,
      files,
      setFiles,
      containerClass,
      handleRemoveFile,
      isAlert: t2,
      isLoadingFile: t3,
      maxSizeInMo,
      disabled: t4,
      ...rest
    } = t0);
    $[0] = t0;
    $[1] = authorizedFiles;
    $[2] = containerClass;
    $[3] = files;
    $[4] = handleRemoveFile;
    $[5] = maxSizeInMo;
    $[6] = rest;
    $[7] = setFiles;
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
  } else {
    authorizedFiles = $[1];
    containerClass = $[2];
    files = $[3];
    handleRemoveFile = $[4];
    maxSizeInMo = $[5];
    rest = $[6];
    setFiles = $[7];
    t1 = $[8];
    t2 = $[9];
    t3 = $[10];
    t4 = $[11];
  }
  const isMultiple = t1 === undefined ? true : t1;
  const isAlert = t2 === undefined ? false : t2;
  const isLoadingFile = t3 === undefined ? false : t3;
  const disabled = t4 === undefined ? false : t4;
  const {
    addErrorToast
  } = useToasts();
  const acceptForDropzone = authorizedFiles as Accept ?? undefined;
  let t5;
  bb0: {
    if (!authorizedFiles) {
      t5 = "";
      break bb0;
    }
    if (typeof authorizedFiles === "string") {
      t5 = authorizedFiles;
      break bb0;
    }
    let t6;
    if ($[12] !== authorizedFiles) {
      const extensionsArray = Object.values(authorizedFiles).flat();
      const sanitizedExtensions = extensionsArray.map(_temp).filter(Boolean);
      t6 = Array.from(new Set(sanitizedExtensions));
      $[12] = authorizedFiles;
      $[13] = t6;
    } else {
      t6 = $[13];
    }
    t5 = t6.join(", ");
  }
  const allowedExtensionsDisplay = t5;
  const t6 = maxSizeInMo ? maxSizeInMo * 1024 * 1024 : undefined;
  let t7;
  if ($[14] !== addErrorToast || $[15] !== files || $[16] !== isMultiple || $[17] !== setFiles) {
    t7 = (acceptedFiles, rejectedFiles) => {
      if (rejectedFiles?.length) {
        addErrorToast(t("file.error"));
        return;
      }
      if (isMultiple) {
        setFiles([...files, ...(acceptedFiles as File[])]);
      } else {
        setFiles(acceptedFiles.length > 0 ? [acceptedFiles[0] as File] : []);
      }
    };
    $[14] = addErrorToast;
    $[15] = files;
    $[16] = isMultiple;
    $[17] = setFiles;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== acceptForDropzone || $[20] !== disabled || $[21] !== isMultiple || $[22] !== t6 || $[23] !== t7) {
    t8 = {
      accept: acceptForDropzone,
      multiple: isMultiple,
      maxSize: t6,
      disabled,
      onDrop: t7
    };
    $[19] = acceptForDropzone;
    $[20] = disabled;
    $[21] = isMultiple;
    $[22] = t6;
    $[23] = t7;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  const {
    getRootProps,
    getInputProps
  } = useDropzone(t8);
  let t9;
  if ($[25] !== getRootProps) {
    t9 = getRootProps({
      className: "dropzone"
    });
    $[25] = getRootProps;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  let t10;
  if ($[27] !== getInputProps || $[28] !== rest) {
    t10 = getInputProps(rest);
    $[27] = getInputProps;
    $[28] = rest;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  let t11;
  if ($[30] !== t10) {
    t11 = <input {...t10} />;
    $[30] = t10;
    $[31] = t11;
  } else {
    t11 = $[31];
  }
  const t12 = isAlert ? "border-danger bg-danger/10 text-danger" : "bg-background";
  let t13;
  if ($[32] !== t12) {
    t13 = mergeTailwindClasses("border-border flex cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed px-3 py-4 opacity-80 transition-all hover:opacity-60", t12);
    $[32] = t12;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  let t14;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <FontAwesomeIcon icon={faDownload} className="h-auto w-[1.1rem]" />;
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  let t15;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <span>{t("file.import")}</span>;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  let t16;
  if ($[36] !== allowedExtensionsDisplay) {
    t16 = allowedExtensionsDisplay && <span className="text-[0.625rem] opacity-80">{`(${allowedExtensionsDisplay})`}</span>;
    $[36] = allowedExtensionsDisplay;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  let t17;
  if ($[38] !== t16) {
    t17 = <h5 className="flex items-center gap-1 text-sm font-bold">{t15}{t16}</h5>;
    $[38] = t16;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  let t18;
  if ($[40] !== maxSizeInMo) {
    t18 = maxSizeInMo && <p className="text-[0.625rem]">{t("file.maxSize", {
        fileSize: maxSizeInMo
      })}</p>;
    $[40] = maxSizeInMo;
    $[41] = t18;
  } else {
    t18 = $[41];
  }
  let t19;
  if ($[42] !== t13 || $[43] !== t17 || $[44] !== t18) {
    t19 = <div className={t13}>{t14}{t17}{t18}</div>;
    $[42] = t13;
    $[43] = t17;
    $[44] = t18;
    $[45] = t19;
  } else {
    t19 = $[45];
  }
  let t20;
  if ($[46] !== containerClass || $[47] !== t11 || $[48] !== t19 || $[49] !== t9) {
    t20 = <div {...t9} className={containerClass}>{t11}{t19}</div>;
    $[46] = containerClass;
    $[47] = t11;
    $[48] = t19;
    $[49] = t9;
    $[50] = t20;
  } else {
    t20 = $[50];
  }
  let t21;
  if ($[51] !== files || $[52] !== handleRemoveFile || $[53] !== isLoadingFile) {
    t21 = (files.length > 0 || isLoadingFile) && <div>{isLoadingFile ? <FileCard file={{
        name: t("loading", {
          ns: "common"
        }),
        type: "application/pdf",
        size: 0
      }} isLoading={true} /> : files.map((file, index) => <FileCard key={index} file={{
        name: file.name,
        type: file.type,
        size: file.size
      }}><button type="button" onClick={() => handleRemoveFile(file)} className="flex items-center"><FontAwesomeIcon icon={faCircleXmark} className="size-4.5 opacity-50" /></button></FileCard>)}</div>;
    $[51] = files;
    $[52] = handleRemoveFile;
    $[53] = isLoadingFile;
    $[54] = t21;
  } else {
    t21 = $[54];
  }
  let t22;
  if ($[55] !== t20 || $[56] !== t21) {
    t22 = <div className="flex flex-col gap-y-1">{t20}{t21}</div>;
    $[55] = t20;
    $[56] = t21;
    $[57] = t22;
  } else {
    t22 = $[57];
  }
  return t22;
};
function _temp(ext) {
  return ext?.startsWith(".") ? ext.slice(1) : ext;
}